import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DocumentsManagement.css';

function DocumentsManagement() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('tous'); // 'tous', 'factures', 'bons_commande'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/documents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = doc.type === 'facture'
        ? `/api/factures/by-numero/${doc.numero}`
        : `/api/bons-commande/${doc.numero}`;

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${doc.numero}.pdf`);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Erreur lors du téléchargement du document');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer ${doc.numero} ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const endpoint = doc.type === 'facture'
        ? `/api/factures/${doc.id}`
        : `/api/bons-commande/${doc.numero}`;

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Document supprimé avec succès');
      fetchDocuments();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const filteredDocuments = documents.filter(doc => {
    // Filtre par type
    let typeMatch = true;
    if (filterType === 'factures') typeMatch = doc.type === 'facture';
    if (filterType === 'bons_commande') typeMatch = doc.type === 'bon_commande';

    // Filtre par recherche
    let searchMatch = true;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      searchMatch = (
        doc.numero?.toLowerCase().includes(search) ||
        doc.client_nom?.toLowerCase().includes(search)
      );
    }

    return typeMatch && searchMatch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const formatMontant = (montant) => {
    if (!montant) return '0.00 €';
    return parseFloat(montant).toFixed(2) + ' €';
  };

  if (loading) {
    return <div className="loading-state">Chargement des documents...</div>;
  }

  return (
    <div className="documents-management">
      <div className="documents-header">
        <h2>Gestion des Documents</h2>
        <div className="header-controls">
          <div className="search-box-docs">
            <input
              type="text"
              placeholder="Rechercher par numéro ou client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filterType === 'tous' ? 'active' : ''}`}
              onClick={() => setFilterType('tous')}
            >
              Tous ({documents.length})
            </button>
            <button
              className={`filter-btn ${filterType === 'factures' ? 'active' : ''}`}
              onClick={() => setFilterType('factures')}
            >
              Factures ({documents.filter(d => d.type === 'facture').length})
            </button>
            <button
              className={`filter-btn ${filterType === 'bons_commande' ? 'active' : ''}`}
              onClick={() => setFilterType('bons_commande')}
            >
              Bons de Commande ({documents.filter(d => d.type === 'bon_commande').length})
            </button>
          </div>
        </div>
      </div>

      {filteredDocuments.length === 0 ? (
        <div className="empty-state">
          {searchTerm ? 'Aucun document ne correspond à votre recherche' : 'Aucun document trouvé'}
        </div>
      ) : (
        <div className="documents-table">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Numéro</th>
                <th>Client</th>
                <th>Date</th>
                <th>Montant TTC</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map((doc) => (
                <tr key={`${doc.type}-${doc.id}`}>
                  <td>
                    <span className={`doc-type-badge ${doc.type === 'facture' ? 'badge-facture' : 'badge-bdc'}`}>
                      {doc.type === 'facture' ? '📄 Facture' : '📋 Bon Commande'}
                    </span>
                  </td>
                  <td className="doc-numero">{doc.numero}</td>
                  <td>{doc.client_nom || 'N/A'}</td>
                  <td>{formatDate(doc.date)}</td>
                  <td className="doc-montant">{formatMontant(doc.montant_ttc)}</td>
                  <td>
                    <div className="actions">
                      <button
                        onClick={() => handleDownload(doc)}
                        className="btn-download"
                        title="Télécharger"
                      >
                        📥 Télécharger
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        className="btn-delete-doc"
                        title="Supprimer"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DocumentsManagement;
